/**
 * Utilitaires pour la gestion des paramètres de page
 * Fusion entre paramètres par défaut et paramètres spécifiques à chaque page
 */

// Paramètres par défaut globaux du test
export const DEFAULT_PAGE_PARAMETERS = {
  layout: {
    columnType: 'single',
    singleColumnWidth: 80,
    contentDistribution: '50-50',
    stimulusPosition: 'left',
    resizableSeparator: false
  },
  // Conservé pour éviter les erreurs même si l'onglet est masqué
  margins: {
    top: 20,
    bottom: 20,
    left: 20,
    right: 20
  },
  stimulus: {
    content: '',
    visible: true,
    sticky: false,
    position: 'top'
  },
  theme: {
    mode: 'light'
  }
};

/**
 * Fusionne les paramètres par défaut avec les paramètres spécifiques d'une page
 * Les paramètres de la page ont la priorité sur les paramètres par défaut
 * 
 * @param {Object} pageParameters - Paramètres spécifiques de la page (peuvent être partiels)
 * @param {Object} defaultParameters - Paramètres par défaut (optionnel)
 * @returns {Object} Paramètres finaux fusionnés
 */
export function mergePageParameters(pageParameters = {}, defaultParameters = DEFAULT_PAGE_PARAMETERS) {
  // Deep merge des objets
  const merged = {
    layout: {
      ...defaultParameters.layout,
      ...(pageParameters.layout || {})
    },
    margins: {
      ...defaultParameters.margins,
      ...(pageParameters.margins || {})
    },
    stimulus: {
      ...defaultParameters.stimulus,
      ...(pageParameters.stimulus || {})
    },
    theme: {
      ...defaultParameters.theme,
      ...(pageParameters.theme || {})
    }
  };

  console.log('🔀 Fusion des paramètres de page:', {
    defaults: defaultParameters,
    pageSpecific: pageParameters,
    merged: merged
  });
  console.log('🔍 DEBUG margins:', {
    defaultMargins: defaultParameters.margins,
    pageMargins: pageParameters.margins,
    mergedMargins: merged.margins,
    hasPageMargins: !!pageParameters.margins
  });

  return merged;
}

/**
 * Valide et nettoie les paramètres de page
 *
 * @param {Object} parameters - Paramètres à valider
 * @returns {Object} Paramètres validés et nettoyés
 */
export function validatePageParameters(parameters) {
  // Deep clone pour éviter les mutations
  const validated = JSON.parse(JSON.stringify(parameters));

  // Validation layout
  if (validated.layout) {
    if (!['single', 'double'].includes(validated.layout.columnType)) {
      validated.layout.columnType = 'single';
    }

    if (validated.layout.singleColumnWidth < 60 || validated.layout.singleColumnWidth > 100) {
      validated.layout.singleColumnWidth = 80;
    }

    if (!['left', 'right'].includes(validated.layout.stimulusPosition)) {
      validated.layout.stimulusPosition = 'left';
    }
  }

  // Validation margins - S'assurer que l'objet existe
  if (validated.margins) {
    ['top', 'bottom', 'left', 'right'].forEach(side => {
      if (typeof validated.margins[side] !== 'number' ||
          validated.margins[side] < 0 ||
          validated.margins[side] > 100) {
        validated.margins[side] = 20;
      }
    });
  }

  // Validation stimulus
  if (validated.stimulus) {
    if (!['top', 'left', 'right', 'bottom'].includes(validated.stimulus.position)) {
      validated.stimulus.position = 'top';
    }
  }

  // Validation theme
  if (validated.theme) {
    if (!['light', 'dark'].includes(validated.theme.mode)) {
      validated.theme.mode = 'light';
    }
  }

  console.log('✅ Paramètres validés:', validated);
  return validated;
}

/**
 * Applique les paramètres à une page spécifique
 *
 * @param {Object} page - Objet page à modifier
 * @param {Object} parameters - Nouveaux paramètres à appliquer
 * @returns {Object} Page modifiée
 */
export function applyParametersToPage(page, parameters) {
  const updatedPage = { ...page };

  // Initialiser parameters si nécessaire
  if (!updatedPage.parameters) {
    updatedPage.parameters = {};
  }

  // Valider les nouveaux paramètres
  const validatedParams = validatePageParameters(parameters);

  // Fusionner en profondeur avec les paramètres existants
  updatedPage.parameters = {
    layout: {
      ...(updatedPage.parameters.layout || {}),
      ...(validatedParams.layout || {})
    },
    margins: {
      ...(updatedPage.parameters.margins || {}),
      ...(validatedParams.margins || {})
    },
    stimulus: {
      ...(updatedPage.parameters.stimulus || {}),
      ...(validatedParams.stimulus || {})
    },
    theme: {
      ...(updatedPage.parameters.theme || {}),
      ...(validatedParams.theme || {})
    }
  };

  console.log('📄 Paramètres appliqués à la page:', {
    pageId: page.id,
    pageLabel: page.label,
    newParameters: updatedPage.parameters
  });

  return updatedPage;
}

/**
 * Applique les paramètres à plusieurs pages
 * 
 * @param {Array} pages - Tableau des pages
 * @param {Object} parameters - Paramètres à appliquer
 * @param {String} scope - 'current', 'next', ou 'all'
 * @param {String} currentPageId - ID de la page actuelle
 * @returns {Array} Tableau des pages modifiées
 */
export function applyParametersToPages(pages, parameters, scope, currentPageId) {
  console.log('🔄 Application des paramètres avec portée:', scope);
  
  if (!Array.isArray(pages)) {
    console.warn('⚠️ Pages n\'est pas un tableau:', pages);
    return pages;
  }

  const currentPageIndex = pages.findIndex(page => page.id === currentPageId);
  
  if (currentPageIndex === -1) {
    console.warn('⚠️ Page actuelle non trouvée:', currentPageId);
    return pages;
  }

  return pages.map((page, index) => {
    let shouldApply = false;

    switch (scope) {
      case 'current':
        shouldApply = index === currentPageIndex;
        break;
      case 'next':
        shouldApply = index > currentPageIndex;
        break;
      case 'all':
        shouldApply = true;
        break;
      default:
        console.warn('⚠️ Portée inconnue:', scope);
        shouldApply = false;
    }

    if (shouldApply) {
      return applyParametersToPage(page, parameters);
    }

    return page;
  });
}

/**
 * Récupère les paramètres effectifs d'une page (fusionnés avec les défauts)
 * 
 * @param {Object} page - Objet page
 * @param {Object} defaultParameters - Paramètres par défaut du test
 * @returns {Object} Paramètres effectifs de la page
 */
export function getEffectivePageParameters(page, defaultParameters = DEFAULT_PAGE_PARAMETERS) {
  const pageParameters = page?.parameters || {};
  return mergePageParameters(pageParameters, defaultParameters);
}